import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import { gt, or } from 'ember-truth-helpers';
import type { Nullable } from 'frontend-utils/addon/utils/nullable';
import changeColorClass from 'lumen/helpers/change-color-class';
import type { Carate, Carecommendation, Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { MEALTYPE_ICONS } from 'lumen/utils/constants/general-constants';
import type { OpenRoomPricingFixedCategory } from 'lumen/utils/constants/pricing-assistant-constants';
import {
  findCarateWithLowestAveragePricePriority,
  isCarateForCategory,
  isCarateForSelectedGuest,
} from 'lumen/utils/pricing-assistant/open-pricing-utils';

import styles from './tooltip-compset-and-recommended-price-list.module.css';

interface HotelPricingAssistantCalendarsDaysTooltipCompsetAndRecommendedPriceListSignature {
  Args: {
    competitorCaratesToShow: Carate[];
    date: Dayjs;
    los: number;
    maxPersons: string;
    ownHotelPricingAssistantRate: Carate;
    ownHotelRecommendation: Carecommendation;
    period: Period;
    selectedRoomCategory: OpenRoomPricingFixedCategory;
    showAutopilotState: boolean;
    showRecommendation: boolean;
  };
}

export default class HotelPricingAssistantCalendarsDaysTooltipCompsetAndRecommendedPriceList extends Component<HotelPricingAssistantCalendarsDaysTooltipCompsetAndRecommendedPriceListSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get caratesToUse(): Carate[] {
    if (!this.args.date || !this.args.period) {
      return [];
    }
    if (!this.access.hasPricingAssistantOpenPricingFeature) {
      return this.args.period.carates.slice();
    }

    const caratesToUse = this.args.competitorCaratesToShow;

    if (this.ownHotelCarateToShow) {
      caratesToUse.push(this.ownHotelCarateToShow);
    }

    return caratesToUse;
  }

  get compsetAndRecommendedPriceList(): Nullable<
    Partial<
      Carate & {
        differentLosThanPreferred: number | null;
        formattedRate?: string;
        isOwnHotel?: boolean;
        isRecommendedPrice?: boolean;
        mealtypeIcon?: IconName;
        name?: string;
        rowClassName?: string;
      }
    >
  >[] {
    const compsetList = this.caratesToUse.map((carate) => {
      const isOwnHotel = this.hotelAndMarketService.isOwnHotelInfo(
        carate.hotelinfo,
      );

      // Hotels that have a soldout/missing rate and no rate value will get pushed to the bottom of the list
      // Note: a CA rate can also have a rate value + a message at the same time: when there's a rate in the PMS, but not on Booking.com
      const sortValue =
        !carate.hasValue && carate.message
          ? -Number.MAX_SAFE_INTEGER
          : carate.value;

      const hasDifferentLosThanRatePreference =
        carate.los &&
        carate.los !== this.hotelAndMarketService.hotel.paRatePreference.los;

      return {
        value: carate.value,
        hasValueAndMessage: carate.hasValueAndMessage,
        sortValue,
        formattedRate: carate.formattedRate,
        message: carate.message,
        formattedMessage: carate.formattedMessage,
        name: carate.hotelinfo.name,
        roomName: carate.roomName,
        mealtypeIcon: MEALTYPE_ICONS[carate.mealtypeIncluded],
        isOwnHotel,
        differentLosThanPreferred: hasDifferentLosThanRatePreference
          ? carate.los
          : null,
        isRecommendedPrice: false,
        priceChangeSinceRefDaysAgo: carate.priceChangeSinceRefDaysAgo,
        formattedPriceChangeSinceRefDaysAgo:
          carate.formattedPriceChangeSinceRefDaysAgo,
        rowClassName: isOwnHotel ? styles['own-hotel-row'] : '',
      };
    });

    const sortedSets = [...compsetList, ...this.recommendedPriceList].sort(
      (a, b) => {
        if (a.sortValue && b.sortValue && b.sortValue - a.sortValue !== 0) {
          return b.sortValue - a.sortValue;
        }

        // sort by message in case the sort values are same but messages are different
        if (a.message !== b.message) {
          return (a.message ?? '').localeCompare(b.message ?? '');
        }

        // sort by hotel name in case both the sort values and messages are same
        return (a.name ?? '').localeCompare(b.name ?? '');
      },
    );

    return sortedSets;
  }

  get ownHotelCarateToShow(): Carate | null {
    const caratesMatchingCategoryAndGuestOwnHotel =
      this.args.period.ownHotelCarates.filter(
        (carate) =>
          isCarateForCategory(carate, this.args.selectedRoomCategory) &&
          isCarateForSelectedGuest(carate, this.args.maxPersons),
      );

    const ownHotelCarateToShow = findCarateWithLowestAveragePricePriority(
      caratesMatchingCategoryAndGuestOwnHotel,
    );

    return ownHotelCarateToShow;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get recommendedPriceList() {
    if (!this.args.showRecommendation) {
      return [];
    }

    // Mock recommendation as the compset list items
    const rowClassName = this.args.showAutopilotState
      ? styles['autopilot-price-row']
      : styles['recommended-price-row'];

    // In case of user edited recommended price, show the overwritten value as new updated price
    const { isRecommendedPriceOverwritten } = this.args.ownHotelRecommendation;

    return [
      {
        value: this.args.ownHotelRecommendation.recommendedValue,
        sortValue: this.args.ownHotelRecommendation.recommendedValue,
        formattedRate:
          this.args.ownHotelRecommendation.formattedRecommendedValue,
        name: this.args.showAutopilotState
          ? this.intl.t('pricing_assistant.autopilot_price')
          : this.intl.t('pricing_assistant.label.recommended_price'),
        message: '',
        roomName: this.args.ownHotelPricingAssistantRate.roomName,
        mealtypeIcon:
          MEALTYPE_ICONS[
            this.args.ownHotelPricingAssistantRate.mealtypeIncluded
          ],
        isOwnHotel: false,
        isRecommendedPrice: true,
        rowClassName,
        ...(isRecommendedPriceOverwritten && {
          value: this.args.ownHotelRecommendation.overwrittenValue,
          sortValue: this.args.ownHotelRecommendation.overwrittenValue,
          formattedRate:
            this.args.ownHotelRecommendation.formattedOverwrittenValue,
          name: this.args.showAutopilotState
            ? this.intl.t('pricing_assistant.autopilot_price')
            : this.intl.t('pricing_assistant.label.new_updated_price'),
        }),
      },
    ];
  }

  get showPriceChangeSinceRefDaysAgoColumn(): boolean {
    if (this.args.date && this.args.period) {
      return [...this.args.period.carates].some(
        (caRate) => caRate.priceChangeSinceRefDaysAgo,
      );
    }

    return false;
  }

  get showRestrictionMessageColumn(): boolean {
    if (this.args.date && this.args.period) {
      return [...this.args.period.carates].some(
        (caRate) => caRate.hasValueAndMessage,
      );
    }

    return false;
  }

  <template>
    <table class={{styles.table}}>
      <thead>
        <tr class="text-200--medium flex flex-row items-center">
          <th class={{styles.property-column}}>
            {{t "general.property.plural" count=1}}
          </th>
          <th class="{{styles.rate-column}} flex justify-end">
            {{#if (gt @los 1)}}
              {{t "pricing_assistant.avg_price"}}
              ({{t "rates.nights" count=@los}})
            {{else}}
              {{t "general.price"}}
            {{/if}}
          </th>
          {{#if this.showRestrictionMessageColumn}}
            <th class={{styles.restriction-message-column}}></th>
          {{/if}}
          {{#if this.showPriceChangeSinceRefDaysAgoColumn}}
            <th class={{styles.price-change-column}}></th>
          {{/if}}
          <th class={{styles.mealtype-icon-column}}></th>
          <th class={{styles.room-name-column}}></th>
        </tr>
      </thead>
      <tbody>
        {{#each this.compsetAndRecommendedPriceList as |listItem|}}
          <tr
            class="text-200--regular flex flex-row items-center
              {{listItem.rowClassName}}"
          >
            <td
              class="{{styles.property-column}}
                {{if
                  (or listItem.isOwnHotel listItem.isRecommendedPrice)
                  'text-200--bold'
                }}
                truncate"
            >
              {{listItem.name}}
            </td>
            <td
              class="{{styles.rate-column}}
                flex justify-end gap-100
                {{if
                  (or listItem.isOwnHotel listItem.isRecommendedPrice)
                  'text-200--bold'
                }}"
            >
              {{#if listItem.differentLosThanPreferred}}
                {{listItem.formattedRate}}
                ({{t "general.los" los=listItem.differentLosThanPreferred}})
              {{else}}
                {{listItem.formattedRate}}
              {{/if}}
            </td>

            {{#if this.showRestrictionMessageColumn}}
              <td
                class="{{styles.restriction-message-column}}
                  {{if
                    (or listItem.isOwnHotel listItem.isRecommendedPrice)
                    'text-200--bold'
                  }}"
              >
                {{#if listItem.hasValueAndMessage}}
                  ({{listItem.formattedMessage}})
                {{/if}}
              </td>
            {{/if}}

            {{! Only show price change column if there's a non null/0 value }}
            {{#if this.showPriceChangeSinceRefDaysAgoColumn}}
              <td
                class="{{styles.price-change-column}}
                  {{changeColorClass listItem.priceChangeSinceRefDaysAgo}}"
              >
                {{! Don't show the value in case it's null (we don't have change information) or 0 (no rate change) }}
                {{#if listItem.priceChangeSinceRefDaysAgo}}
                  {{listItem.formattedPriceChangeSinceRefDaysAgo}}
                {{/if}}
              </td>
            {{/if}}

            <td class={{styles.mealtype-icon-column}}>
              {{#if listItem.mealtypeIcon}}
                <PrismIcon
                  @name={{listItem.mealtypeIcon}}
                  @size="extra-small"
                />
              {{/if}}
            </td>
            <td
              class="{{styles.room-name-column}}
                truncate
                {{if
                  (or listItem.isOwnHotel listItem.isRecommendedPrice)
                  'text-200--bold'
                }}"
            >
              {{listItem.roomName}}
            </td>
          </tr>
        {{/each}}
      </tbody>
    </table>
  </template>
}
